import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import { Link, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { MLPolicyTypeUtil } from 'ml-portals-util-react';
import { MLRiskAnalysisService } from 'ml-services-common-react';

import styles from '../ActivityReportsComponent/ActivityReportsComponent.module.scss';
import messages from './MLContingenciesReportComponent.messages';
import metadata from './MLContingenciesReportComponent.metadata.json5';

const MLContingenciesReportComponent = (props) => {
    const {
        rowLimit,
        isDashboard,
        isCompactView,
        getCustomerDisplayNameRevert,
        history,
        convertLocalDateToDate
    } = props;

    const { getPolicyTypeName } = MLPolicyTypeUtil();
    const translator = useTranslator();
    const { authHeader } = useAuthentication();

    const [isLoading, updateIsLoading] = useState(false);
    const [filteredJobs, updateFilteredJobs] = useState([]);
    const [resultsRowCount, updateResultsRowCount] = useState(0);
    const [filterData, updateFilterData] = useState({});

    const loadContingenciesData = useCallback(async (filteringData) => {
        updateIsLoading(true);

        const response = isDashboard
            ? await MLRiskAnalysisService.getOpenContingenciesForUser(rowLimit, filteringData, authHeader)
            : await MLRiskAnalysisService.getOpenContingenciesForUser(null, filteringData, authHeader);

        updateFilteredJobs(response);
        updateIsLoading(false);
        updateResultsRowCount(response.length);
    }, [authHeader, isDashboard, rowLimit]);

    useEffect(() => {
        loadContingenciesData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onFilter = () => {
        loadContingenciesData(filterData);
    };

    const onClearFilters = () => {
        updateFilterData({});
    };

    const isDateInPast = (date)=> {
        if (!date) {
            return
        };
        return moment(convertLocalDateToDate(date)).isBefore(moment(), 'd');
    };

    const renderDueDateCell = (item) => {
        let dueDateClass = '';
        if (isDateInPast(item.dueDate)) {
            dueDateClass = 'error-text cellValue';
        }
        return item.dueDate ? <span className={dueDateClass}>{convertLocalDateToDate(item.dueDate)}</span> : '-';
    };

    const renderDateAddedCell = (item) => {
        return item.dateAdded ? <span>{convertLocalDateToDate(item.dateAdded)}</span> : '-';
    };

    const renderCustomerName = (item) => {
        if(_.isUndefined(item.accountHolder)) {
            return '-'
        }
        return (
            <Link to={`/accounts/${item.accountNumber}/summary`} className={styles.linkCellValue}>
                {_.capitalize(getCustomerDisplayNameRevert(item.accountHolder))}
            </Link>
        )
    };

    const getProductName = (productCode) => {
        return getPolicyTypeName(productCode);
    };

    const renderProductCell = (item) => {
        return getProductName(item.product.publicID);
    };

    const renderPolicyCell = (item) => {
        if(_.isUndefined(item.policyNumber)) {
            return '-'
        }
        return (
            <Link to={`/policies/${item.policyNumber}/summary`} className={styles.linkCellValue}>
                {item.policyNumber}
            </Link>
        )
    };

    const renderDescriptionCell = (item) => {
        return item.description ? item.description : '-';
    };

    const onViewAllButtonClicked = () => {
        const reportView = 'contingencies-report';
        history.push('/activity-reports', {reportView : reportView} );
    }

    const overrideProps = {
        statusDesc: {
            visible: isLoading,
            content: (<span tabindex="0" role="alert" aria-live="polite">{translator(messages.loading)}</span>)
        },
        mlJobResultsFilterComponent: {
            visible: !isCompactView,
            filterData,
            onFilter,
            onClearFilters,
            resultsRowCount,
            updateFilterData,
            dateLabel: translator(messages.dueDateRange)
        },
        contingenciesReportTable: {
            data: _.orderBy(filteredJobs, 'dueDate', 'asc'),
            visible: !isLoading,
            defaultPageSize: rowLimit,
            noDataText: (<span tabindex="0" role="alert" aria-live="polite">{translator(messages.noContingenciesFound)}</span>),
            showPagination: !isCompactView,
            tableLabel: translator(messages.contingencies)
        },
        viewAllContingenciesButton: {
            visible: isDashboard,
            onClick: onViewAllButtonClicked,
            mlTagManagerLinkClick: "Dashboard_activityReport_viewAll_contingencies"
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            renderDueDateCell,
            renderDateAddedCell,
            renderCustomerName,
            renderProductCell,
            renderPolicyCell,
            renderDescriptionCell
        },
        resolveClassNameMap: styles
    };
    
    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            {...resolvers} />
    );
};

MLContingenciesReportComponent.propTypes = {
    showAgentChanges: PropTypes.bool.isRequired,
    rowLimit: PropTypes.number.isRequired,
    isDashboard: PropTypes.bool.isRequired,
    isCompactView: PropTypes.bool.isRequired,
    getCustomerDisplayNameRevert: PropTypes.func.isRequired,
    history: PropTypes.shape({push: PropTypes.func}).isRequired,
    convertLocalDateToDate: PropTypes.func.isRequired
}

export default withRouter(MLContingenciesReportComponent);
